import { hasAnyAccess } from '@redirector/front/auth/ability';
import ProfileModal from '@redirector/front/components/users/ProfileModal';
import { useAuth, useCurrentUser } from '@redirector/front/context/useAuth';
import { MEDIA_BELOW_MD,useMediaQuery } from '@redirector/front/lib/useMediaQuery';
import type { User } from '@redirector/front/types/types';
import {
  BarChart3,
  Layers,
  Link2,
  LogOut,
  Menu,
  Shield,
  User as UserIcon,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Link2;
  /**
   * Predicate returns true when the current user should see this tab.
   * Runs against the live `user` (memberships + roles), not CASL ability,
   * because tab visibility doesn't involve a specific resource instance.
   *
   * Policy (uniform across all resource tabs): show the tab when the user
   * has ANY action (read/create/update/delete) on the resource in at least
   * one active group. The page itself then scopes data to what they can
   * actually see.
   */
  visible: (user: User, isRootAdmin: boolean) => boolean;
}

// Order: Stats first (default landing), then Redirections, then the
// management surfaces. This matches the post-login flow — users land on
// /stats and read left-to-right to find what else they can do.
const NAV_ITEMS: NavItem[] = [
  {
    to: '/stats',
    label: 'Stats',
    icon: BarChart3,
    visible: (user) => hasAnyAccess(user, 'redirection'),
  },
  {
    to: '/redirections',
    label: 'Redirections',
    icon: Link2,
    visible: (user) => hasAnyAccess(user, 'redirection'),
  },
  {
    to: '/groups',
    label: 'Groups',
    icon: Layers,
    // Anyone with at least one active membership sees the Groups tab. The
    // backend only returns groups the user belongs to for non-root callers.
    visible: (user) => user.memberships.some((m) => !m.isDisabled),
  },
  {
    to: '/users',
    label: 'Users',
    icon: Users,
    visible: (user) => hasAnyAccess(user, 'user'),
  },
  {
    to: '/roles',
    label: 'Roles',
    icon: Shield,
    // Roles are shown to users who can also see the Users tab: the two
    // surfaces are related (user management + the role catalogue they
    // assign). CRUD on roles is super-admin only and enforced inside
    // the page.
    visible: (user) => hasAnyAccess(user, 'user'),
  },
];

export default function Header() {
  const user = useCurrentUser();
  const { isRootAdmin, logout } = useAuth();
  const location = useLocation();
  const [showProfile, setShowProfile] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useMediaQuery(MEDIA_BELOW_MD);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Auto-close the mobile drawer on (a) route change and (b) viewport
  // growing past the breakpoint. We track the previous values in state
  // so we can detect transitions and reset `menuOpen` during render —
  // the React-recommended pattern that supersedes the old "setState
  // inside an effect" approach. Setting state during render is safe as
  // long as the call is conditional (an unconditional one would loop)
  // and aimed at a different state slot than the one being read.
  const [prevPath, setPrevPath] = useState(location.pathname);
  if (prevPath !== location.pathname) {
    setPrevPath(location.pathname);
    setMenuOpen(false);
  }
  const [prevMobile, setPrevMobile] = useState(isMobile);
  if (prevMobile !== isMobile) {
    setPrevMobile(isMobile);
    if (!isMobile) setMenuOpen(false);
  }

  // Click outside the drawer closes it. Bound only when open.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointer = (e: MouseEvent | TouchEvent): void => {
      const node = menuRef.current;
      if (node && !node.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [menuOpen]);

  const visibleItems = NAV_ITEMS.filter((it) => it.visible(user, isRootAdmin));

  const navLink = (item: NavItem, options: { compact?: boolean } = {}) => {
    const isActive = location.pathname.startsWith(item.to);
    const Icon = item.icon;
    return (
      <Link
        key={item.to}
        to={item.to}
        data-testid={`nav-${item.label.toLowerCase()}`}
        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? 'bg-[var(--color-surface-hover)] text-white'
            : 'text-[var(--color-text-muted)] hover:text-white hover:bg-[var(--color-surface-hover)]'
        }`}
      >
        <Icon className="w-4 h-4" />
        <span className={options.compact ? 'hidden sm:inline' : ''}>{item.label}</span>
      </Link>
    );
  };

  // ── Profile cluster (right-hand side; identical desktop + mobile) ─────────

  const profileCluster = (
    <div className="flex items-center gap-2 sm:gap-4">
      <button
        type="button"
        onClick={() => setShowProfile(true)}
        className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        title="Edit profile"
      >
        <div className="text-right hidden md:block">
          <div className="text-sm font-medium text-white flex items-center gap-2 justify-end">
            {user.name}
            {isRootAdmin && (
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-500/20 text-amber-300 rounded"
                title="Super-admin"
              >
                ROOT
              </span>
            )}
          </div>
          <div className="text-xs text-[var(--color-text-muted)]">@{user.username}</div>
        </div>
        {user.avatar ? (
          <img
            src={user.avatar}
            alt="Avatar"
            className="w-9 h-9 rounded-full ring-2 ring-[var(--color-border)]"
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-[var(--color-surface-hover)] flex items-center justify-center">
            <UserIcon className="w-5 h-5 text-[var(--color-text-muted)]" />
          </div>
        )}
      </button>
      <button
        type="button"
        onClick={logout}
        className="p-2 text-[var(--color-text-muted)] hover:text-white hover:bg-[var(--color-surface-hover)] rounded-md transition-colors"
        title="Logout"
      >
        <LogOut className="w-5 h-5" />
      </button>
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <header className="bg-[var(--color-surface)] border-b border-[var(--color-border)] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          {/* Left: brand + (desktop) inline nav, or (mobile) hamburger toggle. */}
          <div className="flex items-center gap-3 sm:gap-6 min-w-0">
            {isMobile && (
              <button
                type="button"
                aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
                aria-expanded={menuOpen}
                aria-controls="primary-navigation"
                onClick={() => setMenuOpen((v) => !v)}
                className="p-2 -ml-2 text-[var(--color-text-muted)] hover:text-white hover:bg-[var(--color-surface-hover)] rounded-md transition-colors"
              >
                {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            )}

            <Link
              to="/"
              className="text-xl font-bold tracking-tight text-white flex items-center gap-2 shrink-0"
            >
              <span className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white font-black">
                R
              </span>
              <span className="hidden sm:inline">Redirector</span>
            </Link>

            {!isMobile && (
              <nav
                id="primary-navigation"
                aria-label="Primary"
                className="flex items-center gap-1 min-w-0 overflow-hidden"
              >
                {visibleItems.map((item) => navLink(item, { compact: true }))}
              </nav>
            )}
          </div>

          {/* Right: profile + logout. Always visible on every viewport. */}
          {profileCluster}
        </div>

        {/* Mobile drawer: full-width slide-down nav. Hidden when not in
          * mobile mode or when collapsed. The drawer takes the full width
          * of the header so each tab is comfortably tap-sized. */}
        {isMobile && menuOpen && (
          <div
            id="primary-navigation"
            ref={menuRef}
            className="border-t border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg"
          >
            <nav aria-label="Primary" className="flex flex-col gap-1 p-3">
              {visibleItems.map((item) => navLink(item))}
            </nav>
          </div>
        )}
      </header>

      {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}
    </>
  );
}
